import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faBuilding,
  faFileAlt,
  faShieldAlt,
  faBriefcase,
  faFolderOpen,
  faFileArchive,
} from '@fortawesome/free-solid-svg-icons';
import { ProfessionCompletedScreenStyles } from './completedScreens.styles';
import {
  CompletedCard,
  CompletedCardHeader,
  CompletedCardIcon,
  CompletedCardTitle,
  CompletedFieldGrid,
  CompletedField,
  CompletedSectionTitle,
} from './CompletedV2.styles';
import {
  apiDownloadDocument,
  documentMethodManualUpload,
} from '../../services/axios-client/axeCommons';
import {
  getProfessionCompletedData,
  professionCompletedDataReset,
} from '../../features/purchases/purchasesActions';
import ErrorComponent from '../../common/components/ErrorComponent';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
import quotationsAxe from '../../services/axios-client/quotationsAxeClient';
import {
  generalAlertError,
} from '../../common/utils/alerts';
import { sonnerSuccessAlert } from '../../common/utils/sonnerAlerts';
import { downloadFile } from '../../services/axios-client/axeHelpers';
import axe from '../../services/axios-client/axiosClient';
import pdfIMG from '../../assets/images/purchase/pdf.png';
import downloadIMG from '../../assets/redesign_icons/download.svg';
import uploadIcon from '../../assets/redesign_icons/upload.svg';
import { ActionsWrapperResultPage } from '../../common/components/redesignComponents/Purchase/SignatureType.styles';
import { getQuotationTypeName } from '../../common/constants';
import ContractDocuments from '../../common/components/elements/ContractDocuments';
import CompletedQuoteScreenAutoAfterSale from '../../common/components/elements/CompletedQuoteScreenAutoAfterSale';

function ProfessionCompletedScreen() {
  const { quoteId } = useParams();
  const [residenceInfo, setResidenceInfo] = useState(null);
  const uploadPolizzaEmessa = useRef(null); // 13 - Polizza emessa
  const uploadQuietanzaSemestrale = useRef(null); // 32 - Quietanza Semestrale
  const uploadPrivacyRef = useRef(null); // 12 - Privacy Greenia
  const uploadPreContractRef = useRef(null); // 29 - Percontrattuale Greenia
  const uploadDichiarazioniDocRef = useRef(null); // 28 - Dichiarazioni del contraente
  const uploadQuestionarioAutoDocRef = useRef(null); // 30 - Questionario Auto
  const uploadTutelaLegaleRef = useRef(null); // 19 - Tutela Legale
  const uploadQuestionarioProfessioneRef = useRef(null); // 33 - Questionario Ptofesionale
  const uploadMergedDocFileRef = useRef(null); // 34 - Merged Files

  const {
    loading, quote, error, insurance_request,
  } = useSelector(
    (store) => store.purchases.profession_completed,
  );

  const { quoteTypes } = useSelector((store) => store.utilities.quoteTypes);
  const { document_types } = useSelector(
    (store) => store.afterSale,
  );

  const collectMunicipalities = () => {
    try {
      quotationsAxe.get('/form-data/communes').then((res) => {
        const commune = res.data.data.find(
          (commune) => commune.cadastral_code
            == insurance_request.customer.residence_commune_code,
        );
        setResidenceInfo(commune);
      });
    } catch (err) {
      // Do nothing
    }
  };

  const collectQuoteData = () => {
    getProfessionCompletedData(quoteId);
  };

  const fireSignedDocUpload = (typeId) => {
    if ([13, 35, 36].includes(typeId)) {
      uploadPolizzaEmessa.current.click();
    }
    if (typeId === 32) {
      uploadQuietanzaSemestrale.current.click();
    }
    if (typeId === 29) {
      uploadPreContractRef.current.click();
    }

    if (typeId === 12) {
      uploadPrivacyRef.current.click();
    }

    if (typeId === 28) {
      uploadDichiarazioniDocRef.current.click();
    }

    if (typeId === 30) {
      uploadQuestionarioAutoDocRef.current.click();
    }

    if (typeId === 19) {
      uploadTutelaLegaleRef.current.click();
    }

    if (typeId === 33) {
      uploadQuestionarioProfessioneRef.current.click();
    }

    if (typeId === 34) {
      uploadMergedDocFileRef.current.click();
    }
  };

  const uploadSignedDocument = async (documentId, files) => {
    const uploadedFile = files[0];
    const reader = new FileReader();

    reader.readAsDataURL(uploadedFile);
    if (uploadedFile.type !== 'application/pdf') {
      generalAlertError('Formato di file errato. Il file deve essere in .pdf');
      return;
    }
    reader.onload = () => {
      documentMethodManualUpload(quote.id, documentId, {
        document: reader.result.replace('data:application/pdf;base64,', ''),
      }).then(() => {
        collectQuoteData();
        sonnerSuccessAlert('File caricato');
      });
    };
  };

  const downloadInvoice = async (quoteId, payment_number) => {
    const res = await axe.get(`/list/invoices/${quoteId}/download/${payment_number}`, {
      responseType: 'blob',
    });
    const contentDisposition = res.headers['content-disposition'];
    const file_name = contentDisposition.split('filename="')[1].split('"')[0];
    downloadFile(res.data, file_name);
  };

  const downloadDocument = async (documentId) => {
    const response = await apiDownloadDocument(documentId);
    const alink = document.createElement('a');
    alink.target = '_blank';
    alink.href = response.data.data.contents;
    alink.download = response.data.data.filename;
    alink.click();
  };

  useEffect(() => {
    collectQuoteData();

    return () => {
      professionCompletedDataReset();
    };
  }, [quoteId]);

  useEffect(() => {
    if (quote && quote.status === 7) {
      collectMunicipalities();
    }
  }, [quote]);

  if (loading) {
    return <PageLoading />;
  }

  if (loading === false && error !== null) {
    return <ErrorComponent error={error} />;
  }

  const documents = [...insurance_request.documents, ...quote.documents];

  return (
    <ProfessionCompletedScreenStyles>
      {/* Contract Documents Section */}
      <div className="section-container">
        <ContractDocuments
          downloadDocument={downloadDocument}
          insuranceRequest={quote.insurance_request}
          quote={quote}
          collectQuoteData={collectQuoteData}
          v2
        />
      </div>

      {/* Customer & Profession Information Section */}
      <div className="section-container">
        <CompletedSectionTitle>
          <FontAwesomeIcon icon={faFileAlt} className="section-icon" />
          <h2>Informazioni Polizza</h2>
        </CompletedSectionTitle>

        <div className="cards-row">
          {/* Customer Information Card */}
          <div>
            <CompletedCard className="polizze-emessa-status-step-one">
              <CompletedCardHeader>
                <CompletedCardIcon>
                  <FontAwesomeIcon
                    icon={insurance_request.customer.gender === 'G' ? faBuilding : faUser}
                  />
                </CompletedCardIcon>
                <CompletedCardTitle>Anagrafica</CompletedCardTitle>
              </CompletedCardHeader>

              <CompletedFieldGrid>
                <CompletedField className="full">
                  <dt>
                    {insurance_request.customer.gender === 'G' ? 'Ragione Sociale' : 'Nome e Cognome'}
                  </dt>
                  <dd>
                    {insurance_request.customer.gender === 'G'
                      ? insurance_request.customer.business_name
                      : `${insurance_request.customer.name} ${insurance_request.customer.surname}`}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Email</dt>
                  <dd>{insurance_request.customer.email}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Telefono</dt>
                  <dd>{insurance_request.customer.phone}</dd>
                </CompletedField>

                <CompletedField className="full">
                  <dt>Indirizzo</dt>
                  <dd>
                    {insurance_request.customer.address}
                    ,
                    {' '}
                    {insurance_request.customer.house_number}
                    ,
                    {' '}
                    {insurance_request.customer.postal_code}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Residenza</dt>
                  <dd>{residenceInfo !== null && residenceInfo.name}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Città</dt>
                  <dd>
                    {residenceInfo !== null && residenceInfo.communal_territory.name}
                  </dd>
                </CompletedField>
              </CompletedFieldGrid>
            </CompletedCard>
          </div>

          {/* Profession Data Card */}
          <div>
            <CompletedCard className="polizze-emessa-status-step-two">
              <CompletedCardHeader>
                <CompletedCardIcon>
                  <FontAwesomeIcon icon={faBriefcase} />
                </CompletedCardIcon>
                <CompletedCardTitle>Dati Professione</CompletedCardTitle>
              </CompletedCardHeader>

              <CompletedFieldGrid>
                {insurance_request.professional.billed && (
                  <CompletedField>
                    <dt>Fatturato</dt>
                    <dd>
                      {parseFloat(
                        insurance_request.professional.billed,
                      ).toLocaleString('it-IT', {
                        minimumFractionDigits: 2,
                      })}
                      {' '}
                      &euro;
                    </dd>
                  </CompletedField>
                )}

                <CompletedField>
                  <dt>Massimale</dt>
                  <dd>
                    {parseFloat(
                      insurance_request.professional.billed_maximum,
                    ).toLocaleString('it-IT', {
                      minimumFractionDigits: 2,
                    })}
                    {' '}
                    &euro;
                  </dd>
                </CompletedField>

                <CompletedField className="full">
                  <dt>Professione</dt>
                  <dd>{insurance_request.professional.profession_desc}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>High Risk</dt>
                  <dd>{insurance_request.professional.high_risk}</dd>
                </CompletedField>
              </CompletedFieldGrid>
            </CompletedCard>
          </div>
        </div>
      </div>

      {/* RC Quote Details Section */}
      <div className="section-container">
        <CompletedSectionTitle>
          <FontAwesomeIcon icon={faShieldAlt} className="section-icon" />
          <h2>Dettagli Responsabilità Civile</h2>
        </CompletedSectionTitle>

        <div className="cards-row">
          <div>
            <CompletedCard className="polizze-emessa-status-step-three">
              <CompletedCardHeader>
                <CompletedCardIcon>
                  <FontAwesomeIcon icon={faShieldAlt} />
                </CompletedCardIcon>
                <CompletedCardTitle>Dati Responsabilità Civile</CompletedCardTitle>
              </CompletedCardHeader>

              <CompletedFieldGrid>
                <CompletedField>
                  <dt>Compagnia</dt>
                  <dd>{quote.company.name}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Stato</dt>
                  <dd>{quote.status === 7 && 'Polizza Emessa'}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Polizza Pagata</dt>
                  <dd>{quote.payment_id !== null && 'SI'}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Premio</dt>
                  <dd>
                    {(
                      parseFloat(quote.amount)
                      - parseFloat(quote.issuance_fee)
                      - (quote.is_facile_quote === 1 ? 20.9 : 0)
                    ).toFixed(2)}
                    {' '}
                    &euro;
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Spese di Emissione</dt>
                  <dd>
                    {(
                      parseFloat(quote.issuance_fee)
                      + (quote.is_facile_quote === 1 ? 20.9 : 0)
                    ).toFixed(2)}
                    {' '}
                    &euro;
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Brokeraggio</dt>
                  <dd>
                    {quote.brokerage_amount.toFixed(2)}
                    {' '}
                    &euro;
                  </dd>
                </CompletedField>
                <CompletedField>
                  <dt>Provvigioni</dt>
                  <dd>
                    {quote.commision.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {' '}
                    &euro;
                  </dd>
                </CompletedField>

                {/* Totale (temporarily hidden)
<CompletedField className="full total">
                  <dt>Totale</dt>
                  <dd>
                    {(quote.amount + quote.brokerage_amount).toFixed(2)}
                    {' '}
                    &euro;
                  </dd>
                </CompletedField>
*/}
              </CompletedFieldGrid>
            </CompletedCard>
          </div>
        </div>
      </div>
      {/* Risk Quotations Section */}
      {quote.risk_quotations.length > 0 && (
        <div className="section-container">
          <CompletedSectionTitle>
            <FontAwesomeIcon icon={faShieldAlt} className="section-icon" />
            <h2>Garanzie Accessorie</h2>
          </CompletedSectionTitle>

          <div className="cards-row">
            {quote.risk_quotations.map((risk_quote, index) => (
              <div key={index}>
                <CompletedCard className="polizze-emessa-status-step-three">
                  <CompletedCardHeader>
                    <CompletedCardIcon>
                      <FontAwesomeIcon icon={faShieldAlt} />
                    </CompletedCardIcon>
                    <CompletedCardTitle>
                      {getQuotationTypeName(risk_quote.quote_type, quoteTypes)}
                    </CompletedCardTitle>
                  </CompletedCardHeader>

                  <CompletedFieldGrid>
                    <CompletedField>
                      <dt>Compagnia</dt>
                      <dd>{risk_quote.company.name}</dd>
                    </CompletedField>

                    <CompletedField>
                      <dt>Stato</dt>
                      <dd>{risk_quote.status === 7 && 'Polizza Emessa'}</dd>
                    </CompletedField>

                    <CompletedField>
                      <dt>Data Decorrenza</dt>
                      <dd>
                        {moment(risk_quote.insurance_request.policy_effective_date)
                          .toDate()
                          .toLocaleDateString('it-IT')}
                      </dd>
                    </CompletedField>

                    <CompletedField>
                      <dt>Frequenza Pagamento</dt>
                      <dd>
                        {risk_quote.payment_frequency === 1 ? 'Annuo' : 'Semestrale'}
                      </dd>
                    </CompletedField>

                    <CompletedField>
                      <dt>Pagato</dt>
                      <dd>
                        {risk_quote.amount_paid === null
                          ? '0.00'
                          : parseFloat(risk_quote.amount_paid).toFixed(2)}
                        {' '}
                        &euro;
                      </dd>
                    </CompletedField>

                    {/* Da Pagare (temporarily hidden)
                    <CompletedField>
                      <dt>Da Pagare</dt>
                      <dd>
                        {risk_quote.amount_paid === risk_quote.amount
                          ? '0.00'
                          : (
                            Math.ceil(risk_quote.amount * 100) / 100
                            - risk_quote.amount_paid
                          ).toFixed(2)}
                        {' '}
                        &euro;
                      </dd>
                    </CompletedField>
                    */}

                    {/* Totale (temporarily hidden)
<CompletedField className="full total">
                      <dt>Totale</dt>
                      <dd>
                        {risk_quote.amount.toFixed(2)}
                        {' '}
                        &euro;
                      </dd>
                    </CompletedField>
*/}
                  </CompletedFieldGrid>
                </CompletedCard>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Documents Section */}
      <div className="section-container mb-4">
        <CompletedCard className="polizze-emessa-status-step-four">
          <CompletedCardHeader>
            <CompletedCardIcon>
              <FontAwesomeIcon icon={faFolderOpen} />
            </CompletedCardIcon>
            <CompletedCardTitle>Documenti</CompletedCardTitle>
          </CompletedCardHeader>
          <div className="documents-body">
            <ul className="documents-list">
              {documents.filter(
                (dr) => dr.document_type_id !== 34 && dr.document_type_id !== 35 && dr.document_type_id !== 36
                  && dr.document_type_id !== 37 && dr.document_type_id !== 38
                  && dr.document_type_id !== 39 && dr.document_type_id !== 40
                  && dr.document_type_id !== 41,
              )
                .map((document, index) => (
                  <li
                    className="document-item"
                    key={index}
                  >
                    <div className="document-content flex-column">
                      <div className="document-name">
                        {document.document_type.name}
                      </div>
                      <div className="document-actions flex-row">
                        <div className="file-info">
                          <img
                            src={pdfIMG}
                            className="img-fluid"
                            width="25"
                            alt="PDF"
                          />
                          <span className="file-name">
                            {document.original_filename}
                          </span>
                        </div>
                        <button
                          className="doc-btn border-0 bg-transparent"
                          onClick={() => downloadDocument(document.id)}
                        >
                          <img src={downloadIMG} width="25" alt="Scarica" />
                        </button>
                        {document.signable_document === 1
                          && (document.is_signed_valid === 0
                            || (document.is_signed_valid === null
                              && document.signed_document_filename === null)) && (
                              <ActionsWrapperResultPage>
                                {' '}
                                {([13, 35, 36].includes(document.document_type.id)) && (
                                <div
                                  className="action-button"
                                  onClick={() => fireSignedDocUpload(document.document_type_id)}
                                >
                                  <img src={uploadIcon} alt="" width={25} />
                                  <input
                                    ref={uploadPolizzaEmessa}
                                    type="file"
                                    name=""
                                    id=""
                                    className="d-none"
                                    onChange={(e) => uploadSignedDocument(
                                      document.id,
                                      e.target.files,
                                    )}
                                  />
                                </div>
                                )}
                                {document.document_type.id === 32 && (
                                <div
                                  className="action-button"
                                  onClick={() => fireSignedDocUpload(document.document_type_id)}
                                >
                                  <img src={uploadIcon} alt="" width={25} />
                                  <input
                                    ref={uploadQuietanzaSemestrale}
                                    type="file"
                                    name=""
                                    id=""
                                    className="d-none"
                                    onChange={(e) => uploadSignedDocument(
                                      document.id,
                                      e.target.files,
                                    )}
                                  />
                                </div>
                                )}
                                {document.document_type.id === 29 && (
                                <div
                                  className="action-button"
                                  onClick={
                                    document.signed_document_filename === null
                                      ? () => fireSignedDocUpload(
                                        document.document_type.id,
                                      )
                                      : () => { }
                                  }
                                >
                                  <img src={uploadIcon} alt="Carica" width={25} />
                                  <input
                                    ref={uploadPreContractRef}
                                    type="file"
                                    name=""
                                    id=""
                                    className="d-none"
                                    onChange={(e) => uploadSignedDocument(
                                      document.id,
                                      e.target.files,
                                    )}
                                  />
                                </div>
                                )}
                                {document.document_type.id === 28 && (
                                <div
                                  className="action-button"
                                  onClick={
                                    document.signed_document_filename === null
                                      ? () => fireSignedDocUpload(
                                        document.document_type.id,
                                      )
                                      : () => { }
                                  }
                                >
                                  <img src={uploadIcon} alt="Carica" width={25} />
                                  <input
                                    ref={uploadDichiarazioniDocRef}
                                    type="file"
                                    name=""
                                    id=""
                                    className="d-none"
                                    onChange={(e) => uploadSignedDocument(
                                      document.id,
                                      e.target.files,
                                    )}
                                  />
                                </div>
                                )}
                                {document.document_type.id === 30 && (
                                <div
                                  className="action-button"
                                  onClick={
                                    document.signed_document_filename === null
                                      ? () => fireSignedDocUpload(
                                        document.document_type.id,
                                      )
                                      : () => { }
                                  }
                                >
                                  <img src={uploadIcon} alt="Carica" width={25} />
                                  <input
                                    ref={uploadQuestionarioAutoDocRef}
                                    type="file"
                                    name=""
                                    id=""
                                    className="d-none"
                                    onChange={(e) => uploadSignedDocument(
                                      document.id,
                                      e.target.files,
                                    )}
                                  />
                                </div>
                                )}
                                {document.document_type.id === 34 && (
                                <div
                                  className="action-button"
                                  onClick={
                                    document.signed_document_filename === null
                                      ? () => fireSignedDocUpload(
                                        document.document_type.id,
                                      )
                                      : () => { }
                                  }
                                >
                                  <img src={uploadIcon} alt="Carica" width={25} />
                                  <input
                                    ref={uploadMergedDocFileRef}
                                    type="file"
                                    name=""
                                    id=""
                                    className="d-none"
                                    onChange={(e) => uploadSignedDocument(
                                      document.id,
                                      e.target.files,
                                    )}
                                  />
                                </div>
                                )}
                                {document.document_type.id === 12 && (
                                <div
                                  className="action-button"
                                  onClick={
                                    document.signed_document_filename === null
                                      ? () => fireSignedDocUpload(
                                        document.document_type.id,
                                      )
                                      : () => { }
                                  }
                                >
                                  <img src={uploadIcon} alt="Carica" width={25} />
                                  <input
                                    ref={uploadPrivacyRef}
                                    type="file"
                                    name=""
                                    id=""
                                    className="d-none"
                                    onChange={(e) => uploadSignedDocument(
                                      document.id,
                                      e.target.files,
                                    )}
                                  />
                                </div>
                                )}
                                {document.document_type.id === 19 && (
                                <div
                                  className="action-button"
                                  onClick={
                                    document.signed_document_filename === null
                                      ? () => fireSignedDocUpload(
                                        document.document_type.id,
                                      )
                                      : () => { }
                                  }
                                >
                                  <img src={uploadIcon} alt="Carica" width={25} />
                                  <input
                                    ref={uploadTutelaLegaleRef}
                                    type="file"
                                    name=""
                                    id=""
                                    className="d-none"
                                    onChange={(e) => uploadSignedDocument(
                                      document.id,
                                      e.target.files,
                                    )}
                                  />
                                </div>
                                )}
                                {document.document_type.id === 33 && (
                                <div
                                  className="action-button"
                                  onClick={
                                    document.signed_document_filename === null
                                      ? () => fireSignedDocUpload(
                                        document.document_type.id,
                                      )
                                      : () => { }
                                  }
                                >
                                  <img src={uploadIcon} alt="Carica" width={25} />
                                  <input
                                    ref={uploadQuestionarioProfessioneRef}
                                    type="file"
                                    name=""
                                    id=""
                                    className="d-none"
                                    onChange={(e) => uploadSignedDocument(
                                      document.id,
                                      e.target.files,
                                    )}
                                  />
                                </div>
                                )}
                              </ActionsWrapperResultPage>
                        )}
                      </div>
                      {document.signable_document === 1
                        && document.is_signed_valid === 0
                        && document.signed_document_filename === null
                        && document.motivation !== null && (
                          <p className="document-motivation">
                            {document.motivation}
                          </p>
                      )}
                    </div>
                    <div className="document-status">
                      {document.signable_document === 1 ? (
                        document.is_signed_valid === null
                          && document.signed_document_filename !== null ? (
                            <span className="status-badge status-warning">
                              In Verifica
                            </span>
                          ) : document.is_signed_valid === 0 ? (
                            <span className="status-badge status-danger">
                              Firma Non Valida
                            </span>
                          ) : document.is_signed_valid === null
                          && document.signed_document_filename === null ? (
                            <span className="status-badge status-warning">
                              Da Firmare
                            </span>
                            ) : document.is_signed_valid === 1
                          && document.signed_document_filename !== null ? (
                            <span className="status-badge status-success">
                              Firmato
                            </span>
                              ) : null
                      ) : (
                        <span className="status-badge status-info">
                          Non Richiede Firma
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              {quote.payments.length > 0 && quote.payments.map((payment, paymentIndex) => (
                <li className="document-item" key={`invoice-${paymentIndex}`}>
                  <div className="document-content flex-column">
                    <div className="document-name">
                      {quote.payments.length > 1
                        ? `Fattura ${paymentIndex + 1}`
                        : 'Fattura'}
                    </div>
                    <div className="document-actions flex-row">
                      <div className="file-info">
                        <img
                          src={pdfIMG}
                          className="img-fluid"
                          width="25"
                          alt="PDF"
                        />
                        <span className="file-name">Scarica Fattura</span>
                      </div>
                      <button
                        className="doc-btn border-0 bg-transparent"
                        onClick={() => downloadInvoice(quote.id, paymentIndex + 1)}
                      >
                        <img src={downloadIMG} width="25" alt="Scarica" />
                      </button>
                    </div>
                  </div>
                  <div className="document-status">
                    <span className="status-badge status-info">
                      Non Richiede Firma
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </CompletedCard>

        <div className="mt-4">
          <CompletedQuoteScreenAutoAfterSale quote={quote} document_types={document_types} v2 />
        </div>

        {quote.initial_quotation !== undefined && (
          <CompletedCard className="mt-3">
            <CompletedCardHeader>
              <CompletedCardIcon>
                <FontAwesomeIcon icon={faFileArchive} />
              </CompletedCardIcon>
              <CompletedCardTitle>Documenti nel preventivo iniziale</CompletedCardTitle>
            </CompletedCardHeader>
            <div className="documents-body">
              <ul className="documents-list">
                {quote.initial_quotation.insurance_request.documents.map(
                  (document, index) => (
                    <li
                      className="document-item"
                      key={index}
                    >
                      <div className="document-content flex-column">
                        <div className="document-name">
                          {document.document_type.name}
                        </div>
                        <div className="document-actions flex-row">
                          <div className="file-info">
                            <img
                              src={pdfIMG}
                              className="img-fluid"
                              width="25"
                              alt="PDF"
                            />
                            <span className="file-name">
                              {document.original_filename}
                            </span>
                          </div>
                          <button
                            className="doc-btn border-0 bg-transparent"
                            onClick={() => downloadDocument(document.id)}
                          >
                            <img src={downloadIMG} width="25" alt="Scarica" />
                          </button>
                        </div>
                      </div>
                      <div className="document-status">
                        {document.signable_document === 1
                          && document.is_signed_valid ? (
                            <span className="status-badge status-success">
                              Firmato
                            </span>
                          ) : (
                            <span className="status-badge status-info">
                              Non Richiede Firma
                            </span>
                          )}
                      </div>
                    </li>
                  ),
                )}
                {quote.initial_quotation.documents.map((document, index) => (
                  <li
                    className="document-item"
                    key={index}
                  >
                    <div className="document-content flex-column">
                      <div className="document-name">
                        {document.document_type.name}
                      </div>
                      <div className="document-actions flex-row">
                        <div className="file-info">
                          <img
                            src={pdfIMG}
                            className="img-fluid"
                            width="25"
                            alt="PDF"
                          />
                          <span className="file-name">
                            {document.original_filename}
                          </span>
                        </div>
                        <button
                          className="doc-btn border-0 bg-transparent"
                          onClick={() => downloadDocument(document.id)}
                        >
                          <img src={downloadIMG} width="25" alt="Scarica" />
                        </button>
                      </div>
                    </div>
                    <div className="document-status">
                      {document.signable_document === 1
                        && document.is_signed_valid ? (
                          <span className="status-badge status-success">
                            Firmato
                          </span>
                        ) : (
                          <span className="status-badge status-info">
                            Non Richiede Firma
                          </span>
                        )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </CompletedCard>
        )}
      </div>
    </ProfessionCompletedScreenStyles>
  );
}

export default ProfessionCompletedScreen;
